import { Fragment, ReactNode, useCallback, useRef, useState } from "react";
import {
  Eye,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FilePreviewDialog } from "@/components/file-preview/FilePreviewDialog";
import { FilePreviewInline } from "@/components/file-preview/FilePreviewInline";
import { useFilePreview } from "@/components/file-preview/hooks";
import { getAttachmentFileUrl } from "@/lib/utils/getAttachmentFileUrl";
import { downloadFileFromUrl } from "@/lib/file";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { buildAttachmentInfoParts } from "@/lib/utils/buildAttachmentInfoParts";
import { AttachmentsNoFilesMessage } from "@/components/AttachmentsNoFilesMessage";

export interface AttachmentFileItem {
  filename: string;
  path?: string;
  url?: string;
  size?: string;
  confirmed_at?: string | null;
}

export interface AttachmentGroup {
  key: string;
  title: string;
  files: AttachmentFileItem[];
}

/** Tryb dostępu do pliku — odpowiada `mode` endpointu `POST …/files/download-url`. */
export type AttachmentFileAccessMode = "download" | "preview";

/**
 * Zwraca adres pliku dla danego trybu albo `null`, gdy adresu nie udało się uzyskać,
 * a użytkownik został już o tym poinformowany (np. toast w hooku mutacji).
 */
export type AttachmentFileUrlResolver = (
  file: AttachmentFileItem,
  mode: AttachmentFileAccessMode,
) => Promise<string | null>;

interface AttachmentsDisplaySectionProps {
  groups: AttachmentGroup[];
  attachmentType?: string;
  canRead?: boolean;
  /**
   * Adres na żądanie (np. presigned GET z backendu, który rejestruje zdarzenie).
   * Gdy podany: brak podglądu inline, a każde pobranie i podgląd woła resolver.
   * Bez niego komponent buduje adres bezpośredni z `url`/`path` jak dotychczas.
   */
  resolveFileUrl?: AttachmentFileUrlResolver;
  /** Dodatkowa linia pod nazwą pliku (np. kto dodał, ostatnie pobranie) */
  renderFileMeta?: (file: AttachmentFileItem) => ReactNode;
  /** Dodatkowe akcje w wierszu pliku (np. „Usuń"), renderowane po przyciskach podglądu */
  renderFileActions?: (file: AttachmentFileItem) => ReactNode;
}

// 36 px na dotyku (WCAG 2.5.8), 24 px od `sm`; `aria-disabled` zamiast `disabled` w trakcie pobierania
// adresu nie zrzuca fokusu z przycisku na <body>.
const ROW_ACTION_CLASS =
  "h-9 w-9 sm:h-6 sm:w-6 touch-manipulation disabled:pointer-events-auto disabled:cursor-not-allowed aria-disabled:cursor-progress";

/** Ikona akcji wiersza; w trakcie pobierania adresu w jej miejscu kręci się wskaźnik. */
const ActionIcon = ({
  icon: Icon,
  busy,
}: {
  icon: LucideIcon;
  busy: boolean;
}) =>
  busy ? (
    <Loader2
      className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
      aria-hidden="true"
    />
  ) : (
    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
  );

export const AttachmentsDisplaySection = ({
  groups,
  attachmentType,
  canRead = true,
  resolveFileUrl,
  renderFileMeta,
  renderFileActions,
}: AttachmentsDisplaySectionProps) => {
  const {
    preview,
    expandedKeys,
    toggleInlinePreview,
    openPreviewDialog,
    closePreview,
  } = useFilePreview();
  const { toast } = useToast();
  const { t } = useTranslation();
  // Wiersze, dla których trwa pobieranie adresu (wiersz → akcja): ref blokuje drugie kliknięcie
  // synchronicznie, stan steruje renderem (wskaźnik, aria-busy).
  const inFlight = useRef(new Map<string, AttachmentFileAccessMode>());
  const [resolving, setResolving] = useState<
    ReadonlyMap<string, AttachmentFileAccessMode>
  >(() => new Map());

  const withResolving = useCallback(
    async (
      rowKey: string,
      mode: AttachmentFileAccessMode,
      action: () => Promise<void>,
    ) => {
      if (inFlight.current.has(rowKey)) return;
      inFlight.current.set(rowKey, mode);
      setResolving(new Map(inFlight.current));
      try {
        await action();
      } catch (error) {
        // Resolver łamiący kontrakt (reject zamiast `null`): bez tego użytkownik nie dostałby żadnej
        // informacji, a onClick zostawiłby nieobsłużone odrzucenie promise'a. To błąd kodu — do Sentry.
        console.error("Failed to resolve file url:", error);
        Sentry.captureException(error);
        toast({
          title: t("errors.genericTitle"),
          description: t("errors.downloadFileFailed"),
          variant: "destructive",
        });
      } finally {
        inFlight.current.delete(rowKey);
        setResolving(new Map(inFlight.current));
      }
    },
    [toast, t],
  );

  const getFileUrl = useCallback(
    (file: AttachmentFileItem, mode: AttachmentFileAccessMode) =>
      resolveFileUrl
        ? resolveFileUrl(file, mode)
        : Promise.resolve(getAttachmentFileUrl(file)),
    [resolveFileUrl],
  );

  const handleDownload = useCallback(
    async (file: AttachmentFileItem, rowKey: string) => {
      await withResolving(rowKey, "download", async () => {
        const fileUrl = await getFileUrl(file, "download");
        if (!fileUrl) return;
        try {
          await downloadFileFromUrl(fileUrl, file.filename);
        } catch (error) {
          console.error("Failed to download file:", error);
          toast({
            title: t("errors.genericTitle"),
            description: t("errors.downloadFileFailed"),
            variant: "destructive",
          });
        }
      });
    },
    [getFileUrl, withResolving, toast, t],
  );

  const handlePreview = useCallback(
    async (file: AttachmentFileItem, rowKey: string) => {
      await withResolving(rowKey, "preview", async () => {
        const fileUrl = await getFileUrl(file, "preview");
        if (!fileUrl) return;
        openPreviewDialog(fileUrl, file.filename);
      });
    },
    [getFileUrl, withResolving, openPreviewDialog],
  );

  const hasAnyFiles = groups.some((g) => g.files.length > 0);

  const renderFileRow = useCallback(
    (file: AttachmentFileItem, rowKey: string) => {
      // Adres bezpośredni tylko bez resolvera; z resolverem wystarczy ścieżka pliku.
      const directUrl = resolveFileUrl ? null : getAttachmentFileUrl(file);
      const hasFile = resolveFileUrl ? Boolean(file.path) : Boolean(directUrl);
      const resolvingMode = resolving.get(rowKey);
      const isResolving = resolvingMode !== undefined;
      const disabled = !canRead || !hasFile;
      // Podgląd inline tylko dla adresów bezpośrednich: przez resolver każdy podgląd jest zdarzeniem.
      const inlinePreviewAvailable = !resolveFileUrl;
      const isExpanded = inlinePreviewAvailable && expandedKeys.has(rowKey);
      const infoParts = buildAttachmentInfoParts({
        confirmed_at: file.confirmed_at,
        size: file.size,
        filePath: file.path ?? file.filename,
      });

      return (
        <Fragment key={rowKey}>
          <tr
            className={cn(
              "odd:bg-white even:bg-gray-100/60 transition-colors duration-150 border-b border-gray-200/80",
              !disabled && !isResolving && "hover:bg-blue-100/40",
            )}
            aria-busy={isResolving || undefined}
          >
            <td className="px-2 py-1 align-top sm:px-3 sm:py-1.5">
              <div className="flex flex-col gap-1 sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-2 min-w-0">
                {/* Na telefonie pełna nazwa (title nie działa na dotyku), od `sm` ucięta z dymkiem. */}
                <span
                  className="text-xs text-gray-900 break-all min-w-0 sm:truncate"
                  title={file.filename}
                >
                  {file.filename}
                </span>
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0 min-w-0 sm:contents">
                  <span className="text-xs text-gray-600 break-words min-w-0">
                    {infoParts.length > 0 ? infoParts.join(" | ") : "-"}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={ROW_ACTION_CLASS}
                      onClick={() => handleDownload(file, rowKey)}
                      disabled={disabled}
                      aria-disabled={isResolving || undefined}
                      aria-label={t("fileAttachments.downloadFile", {
                        filename: file.filename,
                      })}
                      data-testid="attachment-download"
                    >
                      <ActionIcon
                        icon={Download}
                        busy={resolvingMode === "download"}
                      />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={ROW_ACTION_CLASS}
                      onClick={() => handlePreview(file, rowKey)}
                      disabled={disabled}
                      aria-disabled={isResolving || undefined}
                      aria-label={t("fileAttachments.previewFile", {
                        filename: file.filename,
                      })}
                      data-testid="attachment-preview"
                    >
                      <ActionIcon
                        icon={Eye}
                        busy={resolvingMode === "preview"}
                      />
                    </Button>
                    {inlinePreviewAvailable && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={ROW_ACTION_CLASS}
                        onClick={() => toggleInlinePreview(rowKey)}
                        disabled={disabled}
                        aria-expanded={isExpanded}
                        aria-label={t("fileAttachments.toggleInlinePreview", {
                          filename: file.filename,
                        })}
                        data-testid="attachment-inline-toggle"
                      >
                        {isExpanded ? (
                          <ChevronUp
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        ) : (
                          <ChevronDown
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        )}
                      </Button>
                    )}
                    {renderFileActions?.(file)}
                  </div>
                </div>
                {renderFileMeta && (
                  <div
                    className="text-xs text-gray-600 min-w-0 sm:col-span-3"
                    data-testid="attachment-file-meta"
                  >
                    {renderFileMeta(file)}
                  </div>
                )}
              </div>
            </td>
          </tr>
          {isExpanded && directUrl && (
            <tr>
              <td className="p-0">
                <div className="border-t border-gray-200 bg-gray-50/50 px-3 py-2">
                  <FilePreviewInline
                    fileUrl={directUrl}
                    fileName={file.filename}
                  />
                </div>
              </td>
            </tr>
          )}
        </Fragment>
      );
    },
    [
      canRead,
      expandedKeys,
      handleDownload,
      handlePreview,
      renderFileActions,
      renderFileMeta,
      resolveFileUrl,
      resolving,
      t,
      toggleInlinePreview,
    ],
  );

  if (!hasAnyFiles) {
    return <AttachmentsNoFilesMessage attachmentType={attachmentType} />;
  }

  return (
    <div className="space-y-2">
      {groups.map((group) => {
        if (group.files.length === 0) return null;

        const groupFiles = group.files.map((file, fileIndex) => {
          const rowKey = `${group.key}-${file.filename}-${fileIndex}`;
          return renderFileRow(file, rowKey);
        });

        return (
          <div key={group.key} className="overflow-x-auto">
            <table className="w-full min-w-0 border-collapse">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left sm:px-3 sm:py-1.5 border-b-2 border-gray-300">
                    <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                      {group.title}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>{groupFiles}</tbody>
            </table>
          </div>
        );
      })}
      {/* Komunikat dla czytników ekranu w trakcie pobierania adresu: region istnieje od początku, zmienia się treść. */}
      <span role="status" aria-live="polite" className="sr-only">
        {resolving.size > 0 && t("fileAttachments.preparingFile")}
      </span>
      {preview?.fileUrl && (
        <FilePreviewDialog
          open
          onClose={closePreview}
          fileUrl={preview.fileUrl}
          fileName={preview.fileName}
        />
      )}
    </div>
  );
};
